import { useSyncExternalStore } from 'react'
import { type Cue, render } from './cue-synth'

/**
 * Interaction sounds, on until turned off.
 *
 * The rules this follows, in order of how much they matter:
 *
 * On by default, and off the moment somebody says so. What makes this bearable
 * is not the preference but the browser: nothing can play until the page has
 * been interacted with, so arriving at the site is silent no matter what this
 * says, and the first sound anybody hears is a response to something they
 * did. The control is in the header rather than a settings page for the same
 * reason — a site that can make noise should be stoppable in one press.
 *
 * Only on a deliberate action. Opening a game, changing a filter, sending a
 * request, revealing a key: things somebody chose to do. Deliberately nothing
 * on hover or scroll — this is a page people browse for minutes at a time, and
 * a grid of two hundred capsules chirping under the pointer would be
 * unbearable.
 *
 * This file owns the preference; the sounds themselves live in `cue-synth`.
 * Splitting them means a cue can be retuned without going anywhere near the
 * storage and subscription logic, which changes for entirely different
 * reasons and much less often.
 */

export type { Cue }

const STORAGE_KEY = 'game-keys:sound'

/**
 * Only an explicit `off` counts as off.
 *
 * Testing for `on` instead would mean anybody who had already visited stayed
 * silent forever, since nothing was ever written for them — the default has to
 * apply to an absent preference, not just a fresh browser.
 */
function readPreference(): boolean {
  if (typeof localStorage === 'undefined') return true
  return localStorage.getItem(STORAGE_KEY) !== 'off'
}

let enabled = readPreference()

const listeners = new Set<() => void>()

export function setSoundEnabled(value: boolean): void {
  enabled = value

  try {
    localStorage.setItem(STORAGE_KEY, value ? 'on' : 'off')
  } catch {
    // Private browsing, or storage disabled. The preference just will not
    // survive the tab, which is a smaller problem than throwing here.
  }

  for (const listener of listeners) listener()

  // Confirms the choice by making the sound being asked for, and doubles as
  // the user gesture the browser needs before any audio can play at all.
  // `copied` is the brightest cue in the set, which is what suits an on.
  if (value) render('copied')
}

export function isSoundEnabled(): boolean {
  return enabled
}

export function useSoundEnabled(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => enabled,
    // Without storage to read, the default is all there is to report.
    () => true,
  )
}

/** Plays a cue, or does nothing at all — which is the usual case. */
export function cue(name: Cue): void {
  if (!enabled) return
  render(name)
}

/**
 * Plays a cue regardless of the preference, for the admin sound page.
 * Somebody who went to a page of play buttons wants to hear them, whether or
 * not sound is switched on for normal browsing.
 */
export function previewCue(name: Cue): void {
  render(name)
}
